import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/authStore';
import { apiClient } from '../../api/client';

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const { user, updateUser } = useAuthStore();
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [birthDate, setBirthDate] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // 모달이 열릴 때 기존 정보 로드
  useEffect(() => {
    const fetchUserData = async () => {
      if (isOpen && user) {
        try {
          console.log('[EditProfileModal] Fetching user data...');
          // 백엔드에서 사용자 정보 가져오기
          const response = await apiClient.get('/users/me');
          const userData = response.data;
          console.log('[EditProfileModal] User data fetched:', userData);

          setName(userData.name || '');
          setBirthDate(userData.birth_date || '');
          setPhone(userData.phone || '');
          setError('');
          setSuccessMessage('');
        } catch (err: any) {
          console.error('[EditProfileModal] Failed to fetch user data:', err);
          console.error('[EditProfileModal] Error response:', err.response);

          // 401 에러면 에러 메시지 표시
          if (err.response?.status === 401) {
            setError(t('profile.loginRequired'));
          }

          // 에러가 발생해도 기본값 설정
          setName('');
          setBirthDate('');
          setPhone(user.phone || '');
          setSuccessMessage('');
        }
      }
    };

    fetchUserData();
  }, [isOpen, user]);

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers.length <= 3) {
      return numbers;
    }
    if (numbers.length <= 7) {
      return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;
    }
    return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7, 11)}`;
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPhoneNumber(e.target.value);
    setPhone(formatted);
  };

  const formatBirthDate = (value: string) => {
    const numbers = value.replace(/[^\d]/g, '');
    if (numbers.length <= 4) {
      return numbers;
    } else if (numbers.length <= 6) {
      return `${numbers.slice(0, 4)}-${numbers.slice(4)}`;
    } else {
      return `${numbers.slice(0, 4)}-${numbers.slice(4, 6)}-${numbers.slice(6, 8)}`;
    }
  };

  const handleBirthDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatBirthDate(e.target.value);
    setBirthDate(formatted);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!phone.trim()) {
      setError(t('profile.phoneRequired'));
      return;
    }

    const phoneNumbers = phone.replace(/[^\d]/g, '');
    if (phoneNumbers.length !== 11) {
      setError(t('profile.phoneInvalid'));
      return;
    }

    if (!name.trim()) {
      setError(t('profile.nameRequired'));
      return;
    }

    if (!birthDate.trim()) {
      setError(t('profile.birthDateRequired'));
      return;
    }

    const birthDateNumbers = birthDate.replace(/[^\d]/g, '');
    if (birthDateNumbers.length !== 8) {
      setError(t('profile.birthDateInvalid'));
      return;
    }

    setIsSubmitting(true);

    try {
      // 백엔드에 사용자 정보 업데이트 (/me 엔드포인트 사용)
      // JWT 토큰으로 사용자를 식별하므로 user.id 체크 불필요
      const response = await apiClient.patch('/users/me', {
        name: name.trim(),  // 실명
        phone: phone.trim(),
        birth_date: birthDate.trim(),  // 생년월일
      });

      // 로컬 스토어 업데이트 (전화번호만 저장)
      updateUser({
        phone: phone.trim(),
      });

      setSuccessMessage(t('profile.updateSuccess'));

      setIsSubmitting(false);

      // 1.5초 후 모달 닫기
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err: any) {
      console.error('Failed to update profile:', err);

      // 에러 메시지 처리
      let errorMessage = t('profile.updateFailed');
      if (err.response?.status === 401) {
        errorMessage = t('profile.loginRequired');
      } else if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      }

      setError(errorMessage);
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{t('profile.editTitle')}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* User Info */}
          {(user?.nickname || user?.email) && (
            <div className="p-3 bg-gray-50 rounded-lg mb-6">
              <div className="space-y-2">
                {user?.nickname && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                    <span>{t('profile.kakaoNickname')}: <strong>{user.nickname}</strong></span>
                  </div>
                )}
                {user?.email && (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>{user.email}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">
                  {t('profile.realName')} <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-500">{t('profile.realNameHelp')}</span>
              </div>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('profile.realNamePlaceholder')}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Birth Date */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">
                  {t('profile.birthDate')} <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-500">{t('profile.birthDateHelp')}</span>
              </div>
              <input
                type="text"
                value={birthDate}
                onChange={handleBirthDateChange}
                placeholder="1990-01-01"
                maxLength={10}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Phone */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-gray-700">
                  {t('profile.phone')} <span className="text-red-500">*</span>
                </label>
                <span className="text-xs text-gray-500">{t('profile.phoneHelp')}</span>
              </div>
              <input
                type="tel"
                value={phone}
                onChange={handlePhoneChange}
                placeholder="010-1234-5678"
                maxLength={13}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                required
              />
            </div>

            {/* Success Message */}
            {successMessage && (
              <div className="p-3 bg-green-50 text-green-700 rounded-lg text-sm flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {successMessage}
              </div>
            )}

            {/* Error */}
            {error && (
              <div className="p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-white text-gray-700 py-3 rounded-lg font-medium border border-gray-300 hover:bg-gray-50 transition-colors"
              >
                {t('common.cancel')}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
              >
                {isSubmitting ? t('profile.saving') : t('profile.save')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditProfileModal;
